import { useRef, useCallback, useLayoutEffect, type RefObject } from 'react';
import { useResizeObserver } from 'owa-react-hooks/lib/useResizeObserver';

interface UseHeaderResponsiveProps {
    searchBoxRespond?: (step: number) => void;
    currentModule?: string;
}

export default function useHeaderResponsive(
    breakpoint: number,
    props?: UseHeaderResponsiveProps
): RefObject<HTMLDivElement> {
    const ref = useRef<HTMLDivElement>(null);
    // null means "not yet observed" so the very first ResizeObserver callback always fires
    // searchBoxRespond, even when the header loads already below the breakpoint (e.g. a refresh
    // at high zoom + narrow width). A boolean seed would swallow that first call when the initial
    // state matched the seed, leaving the search box stuck in its non-compact layout.
    const wasAboveBreakpointRef = useRef<boolean | null>(null);
    const lastModuleRef = useRef<string | undefined>(props?.currentModule);
    const lastBoundsRef = useRef<DOMRectReadOnly | null>(null);

    const handleResize = useCallback(
        (bounds: DOMRectReadOnly) => {
            const isAboveBreakpoint = bounds.width >= breakpoint;
            const wasAboveBreakpoint = wasAboveBreakpointRef.current;
            const currentModule = props?.currentModule;
            const lastModule = lastModuleRef.current;
            const moduleChanged = currentModule !== lastModule;

            // Store the latest bounds for future use
            lastBoundsRef.current = bounds;

            // Only process if there's a state change or module change
            if (wasAboveBreakpoint !== isAboveBreakpoint || moduleChanged) {
                // Call searchBoxRespond with appropriate step value
                if (props?.searchBoxRespond) {
                    // Call with 0 when below/equal breakpoint, 1 when above breakpoint
                    props.searchBoxRespond(isAboveBreakpoint ? 1 : 0);
                }

                // Update the ref to track current state
                wasAboveBreakpointRef.current = isAboveBreakpoint;
                lastModuleRef.current = currentModule;
            }
        },
        [breakpoint, props?.searchBoxRespond, props?.currentModule]
    );

    // Force re-evaluation when module changes using last known bounds
    useLayoutEffect(() => {
        if (props?.currentModule !== lastModuleRef.current && lastBoundsRef.current) {
            handleResize(lastBoundsRef.current);
        }
    }, [props?.currentModule, handleResize]);

    useResizeObserver('OwaHeader', ref, handleResize, { debounce: 100 });

    return ref;
}
